import React, { useState } from 'react';
import { isEmpty } from 'lodash';
import { useAtomValue } from 'jotai';
import { GigServiceCardListings } from '@fiverr-private/listings';
import { useBreakpoints } from '@fiverr-private/business_blocks';

import {
    useMonitoring,
    FederatedSeller,
    useSellerData,
    useLocalizationContext,
    useCurrentPageName,
    ProActionButtons,
    HourlyOrderCard,
    MonitoringSources,
    useIsUserOnline,
    useHydrationState,
} from '@fiverr-private/seller_pages_toolbox';
import { Stack } from '@fiverr-private/layout_components';
import { useSellerDisplayName } from '../../hooks/useSellerDisplayName';
import { useGigsWithoutRating } from '../../hooks/useGigsWithoutRating';
import { gigsAtom, rolloutsAtom } from '../../atoms';
import { ROLLOUTS } from '../../../server/middlewares/addRolloutsData/constants';
import { isBrowser } from './utils';
import { useGigsOrdered, useGigsSliced } from './useGigs';
import { ExpansionControl } from './ExpansionControl';
import { GigServicesHeader } from './GigServicesHeader';
import { GigServicesErrorBoundary } from './GigServicesErrorBoundary';

interface GigServicesProps {
    sellerRoleId?: string;
    showTranslatedUGC: boolean;
    title?: React.ReactNode;
}

// Used as real value in brif generation, hence no translations
const AI_BRIEF_GENERATION_TEXT = 'Can we work on hourly basis on ';

export const GigServices: React.FC<GigServicesProps> = ({ sellerRoleId, showTranslatedUGC, title }) => {
    const { username, hourlyRate, displayName, seller } = useSellerData(selectSellerSlice);
    const sellerDisplayName = useSellerDisplayName();
    const rollouts = useAtomValue(rolloutsAtom);
    const inCobrasOpenHourlyToMpRollout = rollouts[ROLLOUTS.COBRAS_OPEN_HOURLY_TO_MP];
    const showHourlyOrderCard = inCobrasOpenHourlyToMpRollout && hourlyRate;
    const { componentKey } = useHydrationState('GigServices');

    const { currency } = useLocalizationContext();
    const { fireEvents, Providers } = useMonitoring();
    const pageName = useCurrentPageName();
    const [showAllGigs, setShowAllGigs] = useState(false);

    const isOnline = useIsUserOnline(seller?.user?.name);
    const countCollapsed = useCountCollapsed();
    const sellerGigs = useAtomValue(gigsAtom);

    const gigs = useGigsOrdered(sellerRoleId);
    const gigsToShow = useGigsWithoutRating(useGigsSliced(gigs, showAllGigs, countCollapsed));

    const showAllButton = gigs.length > countCollapsed;

    const layout = useLayout();

    const onViewAll = () => {
        fireEvents({
            [Providers.BigQuery]: {
                type: 'seller_page_clicked_on_show_more_service',
            },
            [Providers.Mixpanel]: {
                type: 'click see all services',
            },
        });
        setShowAllGigs(true);
    };

    const onViewLess = () => {
        fireEvents({
            [Providers.BigQuery]: {
                type: 'clicked_on_show_less_services_role_page',
            },
            [Providers.Mixpanel]: {
                type: 'click see less services',
            },
        });
        setShowAllGigs(false);
    };

    if (isEmpty(gigs)) {
        return null;
    }

    return (
        <GigServicesErrorBoundary>
            <Stack direction="column" gap="4" key={componentKey}>
                <GigServicesHeader isOverviewTab={!sellerRoleId} title={title} />
                <GigServiceCardListings
                    openInNewTab
                    source={pageName}
                    currency={currency}
                    items={gigsToShow}
                    showTranslatedUGC={showTranslatedUGC}
                    layout={layout}
                    sellerUsername={username}
                    sellerDisplayName={sellerDisplayName}
                    renderCustomCard={
                        showHourlyOrderCard
                            ? () => (
                                  <ProActionButtons.ContactButton
                                      aiBriefOnly
                                      fullWidth
                                      fullHeight
                                      isOnline={isOnline}
                                      disableProMigration
                                      seller={seller}
                                      gigs={sellerGigs}
                                      source={MonitoringSources.page}
                                      aiBriefInitialGenerationText={AI_BRIEF_GENERATION_TEXT}
                                      renderTrigger={() => (
                                          <HourlyOrderCard
                                              hourlyRate={hourlyRate / 100}
                                              sellerName={displayName || ''}
                                          />
                                      )}
                                  />
                              )
                            : null
                    }
                />
                {showAllButton && (
                    <ExpansionControl
                        expanded={showAllGigs}
                        totalCount={gigs.length}
                        onExpand={onViewAll}
                        onCollapse={onViewLess}
                    />
                )}
            </Stack>
        </GigServicesErrorBoundary>
    );
};

function selectSellerSlice(seller: FederatedSeller) {
    return {
        username: seller.user.name,
        hourlyRate: seller.hourlyRate?.priceInCents,
        displayName: seller.user.profile.displayName,
        seller,
    };
}

const useCountCollapsed = (): number => {
    const { isLG } = useBreakpoints();
    return isLG ? 4 : 2;
};

const useLayout = () => {
    const { isMD } = useBreakpoints();
    const showGridGigs = !isBrowser() || isMD;
    const layout = showGridGigs ? GigServiceCardListings.LAYOUT.GRID : GigServiceCardListings.LAYOUT.LIST_HORIZONTAL;
    return layout;
};
